import { v4 as uuidv4 } from "uuid";
import { uploadFileToS3Complete } from "../../../api/s3";
import {
    AudioAI,
    AudioClipDialogLine,
    AudioClipLine,
    AudioClipVoice,
    AudioComposition,
    AudioCompositionClip,
    AudioEditorClipRole,
    AudioEditorClipSourceKind,
    AudioEditorTrackType,
    SoundLibraryAI,
} from "../../../types/types";
import { addItemToProjectAI, createAudioStudioAI, getAudioComposition, updateAudioComposition } from "../../../api";
import { ImportedAudioEditorTrackType } from "../../../context/AudioEditorContext";
import { GetAudioCompositionResponse } from "../../../types/requestsOutputs";
import { NotificationType } from "../../../context/NotificationContext";

// Type predicates for determining audio type
export function isSoundLibraryAI(obj: any): obj is SoundLibraryAI {
    return obj && typeof obj.url === "string" && obj.url.length > 0;
}

export function isAudioAI(obj: any): obj is AudioAI {
    return obj && typeof obj.audioUrl === "string" && obj.audioUrl.length > 0;
}

/**
 * Check that url exists and it's not an empty string
 */
export function audioHasURL(audio: AudioAI | SoundLibraryAI): boolean {
    return (isSoundLibraryAI(audio) && !!audio.url) || (isAudioAI(audio) && !!audio.audioUrl);
}

/**
 * SoundLibraryAIType rows are seeded and carry the same ids across every database
 * variant, so the id is the authoritative match. The canonical (non-localized) names are
 * kept as a second chance in case a variant ever diverges, and anything unrecognised
 * falls back to a plain "sound" rather than being mislabelled a sound effect.
 *
 * This only drives a colour, an icon and a label — never the audio or what is stored.
 */
const SOUND_TYPE_ID_ROLES: Record<string, AudioEditorClipRole> = {
    cmbe369n90006zxb2i7qvfetk: "music",
    cmbgxk8y30009x0lfhev6oxdt: "sfx",
};

const SOUND_TYPE_NAME_ROLES: Record<string, AudioEditorClipRole> = {
    music: "music",
    "sound design": "sfx",
};

const resolveSoundLibraryRole = (audio: SoundLibraryAI): AudioEditorClipRole => {
    const typeId = audio.soundLibraryAIType?.id || audio.soundLibraryAITypeId;
    if (typeId && SOUND_TYPE_ID_ROLES[typeId]) return SOUND_TYPE_ID_ROLES[typeId];

    // The canonical name, not the localized one: localizations differ per language.
    const typeName = (audio.soundLibraryAIType?.name || audio.type || "").trim().toLowerCase();
    return SOUND_TYPE_NAME_ROLES[typeName] || "sound";
};

/**
 * Who recorded a capsule. Undefined when it says nothing, so an empty object never ends
 * up on the clip — same rule as the server-side normalization.
 */
export const voiceFromAudioAI = (audio: AudioAI): AudioClipVoice | undefined => {
    const voice: AudioClipVoice = {
        voiceAIId: audio.voiceAIId ?? null,
        voiceAISecondId: audio.voiceAISecondId ?? null,
        voiceCloneId: audio.voiceCloneId ?? null,
        voiceCloneSecondId: audio.voiceCloneSecondId ?? null,
        voicePromptId: audio.voicePromptId ?? null,
        voicePromptSecondId: audio.voicePromptSecondId ?? null,
        // A recording that has no voice row of ours is named by hand, on the upload
        // screen or on the item edit screen. That name is a mapping key in its own right
        // at translation time, so it has to travel with the clip like a real voice does.
        voiceLabel: audio.voiceLabel ?? null,
        isDialog: audio.isDialog === true,
        name: audio.voiceAI?.name ?? null,
    };
    const saysSomething =
        voice.voiceAIId || voice.voiceCloneId || voice.voicePromptId || voice.voiceLabel || voice.isDialog;
    return saysSomething ? voice : undefined;
};

/**
 * Converts an Audio object (either SoundLibraryAI or AudioAI) to the unified AudioEditorTrackType type
 */
export interface ConvertAudioOptions {
    /** What the object really is, when its shape cannot say. */
    sourceKind?: AudioEditorClipSourceKind;
    /** Who recorded it, carried from the source capsule. */
    voice?: AudioClipVoice;
    /** Transcript of the source capsule. */
    text?: string | null;
    /** Turns of the source capsule, when it was itself a dialogue. */
    dialogLines?: AudioClipDialogLine[];
}

export function convertAudioToEditor(
    audio: SoundLibraryAI | AudioAI,
    options?: AudioEditorClipSourceKind | ConvertAudioOptions,
): ImportedAudioEditorTrackType {
    // Accept the bare source kind too: the drop zone only has that to say.
    const {
        sourceKind: sourceKindOverride,
        voice,
        text,
        dialogLines,
    } = typeof options === "string" ? ({ sourceKind: options } as ConvertAudioOptions) : (options ?? {});

    if (isSoundLibraryAI(audio) && audioHasURL(audio)) {
        // Two callers hand us a SoundLibraryAI-shaped object that is not a library sound:
        // the drop zone (a local file, so no library row and no id to claim) and the
        // project picker, which flattens a capsule into this shape and would otherwise
        // lose the fact that it is a voice, along with its transcript.
        const kind: AudioEditorClipSourceKind = sourceKindOverride ?? "soundLibrary";
        const role: AudioEditorClipRole =
            kind === "audioAI" ? "voice" : kind === "upload" ? "sound" : resolveSoundLibraryRole(audio);
        return {
            id: audio.id,
            name: audio.name,
            url: audio.url,
            description: audio.soundDescription,
            role,
            sourceKind: kind,
            sourceId: kind === "upload" ? null : audio.id,
            ...(kind === "audioAI" ? { sourceText: text ?? audio.soundDescription ?? null } : {}),
            ...(voice ? { sourceVoice: voice } : {}),
            ...(dialogLines && dialogLines.length > 0 ? { sourceDialogLines: dialogLines } : {}),
            editorUID: uuidv4(),
        };
    } else if (isAudioAI(audio) && audioHasURL(audio)) {
        return {
            id: audio.id,
            name: audio.name,
            url: audio.audioUrl,
            description: audio.text,
            role: "voice",
            sourceKind: "audioAI",
            sourceId: audio.id,
            sourceText: audio.text ?? null,
            ...((voice ?? voiceFromAudioAI(audio)) ? { sourceVoice: voice ?? voiceFromAudioAI(audio) } : {}),
            editorUID: uuidv4(),
        };
    }

    // This should never happen if the type predicates are working correctly
    throw new Error("Invalid audio type");
}

/**
 * Read a fade off a timeline track.
 *
 * waveform-playlist overwrites `track.fadeIn` / `track.fadeOut` with its own fade **id**
 * once a fade is applied — that is why the editor calls `track.removeFade(track.fadeIn)`.
 * The editor therefore keeps its own copy in `_appliedFades`, and that is what has to be
 * persisted: reading the library field would store a meaningless id string.
 */
const readFade = (
    track: AudioEditorTrackType,
    kind: "fadeIn" | "fadeOut",
): { shape: string; start: number; duration: number } | undefined => {
    const applied = track._appliedFades?.[kind];
    if (applied && typeof applied.duration === "number" && applied.duration > 0) {
        // The editor only ever applies linear fades.
        return { shape: "linear", start: applied.start ?? 0, duration: applied.duration };
    }

    // Fall back to the field only while it still holds our own object (before the
    // library has taken it over).
    const raw = track[kind] as unknown;
    if (raw && typeof raw === "object") {
        const fade = raw as { shape?: string; start?: number; duration?: number };
        if (typeof fade.duration === "number" && fade.duration > 0) {
            return { shape: fade.shape || "linear", start: fade.start ?? 0, duration: fade.duration };
        }
    }
    return undefined;
};

/**
 * The editor session for an audio: what the modal needs to know about the row being
 * edited. Defined once so the bootstrap and the post-save refresh cannot drift apart.
 */
export const toEditorSession = (payload: GetAudioCompositionResponse) => ({
    audioAIId: payload.voiceAudio.id,
    name: payload.voiceAudio.name,
    language: payload.voiceAudio.language,
    transcript: payload.voiceAudio.text ?? null,
    voiceAIId: payload.voiceAudio.voiceAIId ?? null,
    voiceCloneId: payload.voiceAudio.voiceCloneId ?? null,
    voicePromptId: payload.voiceAudio.voicePromptId ?? null,
    inheritedFromLanguage: payload.inherited ? payload.sourceLanguage : null,
});

/**
 * Read the session back from the server.
 *
 * Used after saving in place: the session still describes what was loaded before, so the
 * modal would reopen on the previous version. Re-reading rather than patching locally is
 * what gives the newly created replies their real ids — without them a second save would
 * create duplicates instead of updating.
 */
export async function fetchEditorSession(token: string, audioAIId: string) {
    const payload = await getAudioComposition({ token, audioAIId });
    return payload?.voiceAudio ? toEditorSession(payload) : null;
}

/** Length of a clip on the timeline, in seconds. */
const clipLength = (track: Pick<AudioEditorTrackType, "cueIn" | "cueOut">) =>
    Math.max(0, (track.cueOut ?? 0) - (track.cueIn ?? 0));

/** Where a clip ends on the timeline. */
const clipEnd = (track: AudioEditorTrackType) => (track.startTime ?? 0) + clipLength(track);

/**
 * Serialize the timeline into the arrangement stored alongside the rendered mix.
 *
 * One entry per clip, not per asset: the same sound laid twice yields two independent
 * entries, and a split track yields two entries sharing a url with complementary
 * cueIn/cueOut. `voiceDuration` is the anchor used to rescale positions when the same
 * sound design is replayed over a translated voice of a different length.
 */
/** How a clip is identified across the timeline and the transcript cards. */
export const clipKey = (track: Pick<AudioEditorTrackType, "sourceId" | "url">) => track.sourceId || track.url;

/**
 * @param linesByClip What the user validated, keyed by clip id. A clip that gets blocks
 *   is speech — a translation replaces it with its own voice; one that gets none is
 *   sound design and is kept. Omitted, the clip keeps whatever it already carries.
 */
export function buildComposition(
    tracks: AudioEditorTrackType[],
    linesByClip?: Record<string, AudioClipLine[]>,
): AudioComposition | null {
    if (!tracks.length) return null;

    const linesFor = (track: AudioEditorTrackType): AudioClipLine[] =>
        (linesByClip ? linesByClip[track.id] : track.lines) || [];

    const roleOf = (track: AudioEditorTrackType): AudioEditorClipRole => {
        const detected = track.role ?? "sound";
        if (linesFor(track).length > 0) return "voice";
        // Imported from a capsule but left without a text: sound design, not narration.
        return detected === "voice" ? "sound" : detected;
    };

    const clips: AudioCompositionClip[] = tracks.map((track, index) => {
        const clip: AudioCompositionClip = {
            clipId: track.id || `clip-${index}`,
            role: roleOf(track),
            sourceKind: track.sourceKind ?? "upload",
            sourceId: track.sourceId ?? null,
            url: stripNocache(track.url),
            name: track.name,
            startTime: track.startTime ?? 0,
            cueIn: track.cueIn ?? 0,
            cueOut: track.cueOut ?? 0,
            gain: track.gain ?? 1,
            muted: track.muted === true,
        };
        const lines = linesFor(track);
        if (lines.length > 0) clip.lines = lines;
        if (clip.role === "voice" && track.sourceVoice) clip.voice = { ...track.sourceVoice };

        const fadeIn = readFade(track, "fadeIn");
        if (fadeIn) clip.fadeIn = fadeIn;
        const fadeOut = readFade(track, "fadeOut");
        if (fadeOut) clip.fadeOut = fadeOut;
        return clip;
    });

    const voiceTracks = tracks.filter((track) => roleOf(track) === "voice");
    // Span of the speech itself: from where it starts on the timeline to where it ends.
    // Not the timeline end — a voice that only starts at 4s would otherwise be measured
    // 4s longer than it is, and a translated voice would be rescaled against that.
    const voiceStart = voiceTracks.reduce((start, track) => Math.min(start, track.startTime ?? 0), Infinity);
    const voiceEnd = voiceTracks.reduce((end, track) => Math.max(end, clipEnd(track)), 0);
    const voiceDuration = voiceTracks.length > 0 ? voiceEnd - voiceStart : 0;

    return {
        // Null when there is no voice, or when it has not been decoded yet: nothing to
        // rescale against. The arrangement is still worth saving so the mix can be
        // reopened as it was; only inheriting it onto a translation needs the anchor.
        voiceDuration: voiceDuration > 0 ? voiceDuration : null,
        totalDuration: tracks.reduce((end, track) => Math.max(end, clipEnd(track)), 0),
        // A voice split in several clips, or trimmed from its own start, cannot have its
        // cut points transposed onto another language.
        hasVoiceCuts: voiceTracks.length > 1 || voiceTracks.some((track) => (track.cueIn ?? 0) > 0),
        tracks: clips,
    };
}

/** One capsule whose voice is on the timeline, with the transcript it came with. */
export interface VoiceSourceSummary {
    sourceId: string;
    name: string;
    text: string;
}

/**
 * The capsules whose voice is used on the timeline, in timeline order, deduplicated.
 * Feeds the transcript step: the user validates or reworks those texts so the mix keeps
 * a transcript that actually matches what is heard, and stays translatable.
 */
export function collectVoiceSources(tracks: AudioEditorTrackType[]): VoiceSourceSummary[] {
    const seen = new Set<string>();
    return (
        [...tracks]
            // Provenance, not role: a capsule left without a text is still the capsule this
            // mix descends from.
            .filter((track) => track.sourceKind === "audioAI" && track.sourceId)
            .sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0))
            .reduce<VoiceSourceSummary[]>((sources, track) => {
                const sourceId = track.sourceId as string;
                if (seen.has(sourceId)) return sources;
                seen.add(sourceId);
                sources.push({ sourceId, name: track.name, text: track.sourceText ?? "" });
                return sources;
            }, [])
    );
}

/**
 * Voices to record on a mix, gathered from the voice clips on the timeline.
 *
 * Fallback only. The server derives the voice of a mix from the blocks of speech the
 * user validated, like the text and the replies — reading it off the clips attributes
 * the mix by provenance, which names a capsule dropped in purely as ambience. This is
 * what remains for a mix that carries no speech at all, where there is no block to read.
 *
 * One voice source keeps its primary and secondary voices as they were. A montage of
 * several capsules keeps the first two distinct voices and is flagged as a dialog.
 * The full per-clip detail stays in the composition either way.
 */
export function resolveMixVoices(tracks: AudioEditorTrackType[]): {
    voiceAIId: string | null;
    voiceAISecondId: string | null;
    voiceCloneId: string | null;
    voiceCloneSecondId: string | null;
    voicePromptId: string | null;
    voicePromptSecondId: string | null;
    isDialog: boolean;
} {
    const voices = tracks
        .filter((track) => track.role === "voice" && track.sourceVoice)
        .map((track) => track.sourceVoice as AudioClipVoice);

    const seen = new Set<string>();
    const distinct = voices.filter((voice) => {
        const key = [voice.voiceAIId, voice.voiceCloneId, voice.voicePromptId].join("|");
        if (key === "||" || seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    const [first, second] = distinct;
    return {
        voiceAIId: first?.voiceAIId ?? null,
        voiceCloneId: first?.voiceCloneId ?? null,
        voicePromptId: first?.voicePromptId ?? null,
        // The second slot goes to the source's own secondary voice when it had one (it
        // was a dialog), otherwise to the second distinct voice of the montage. Each kind
        // keeps its own column, so a clone or a designed voice is not dropped.
        voiceAISecondId: first?.voiceAISecondId ?? second?.voiceAIId ?? null,
        voiceCloneSecondId: first?.voiceCloneSecondId ?? second?.voiceCloneId ?? null,
        voicePromptSecondId: first?.voicePromptSecondId ?? second?.voicePromptId ?? null,
        isDialog: distinct.length > 1 || voices.some((voice) => voice.isDialog === true),
    };
}

/** Where the speech starts on the timeline: the pivot every repositioning turns around. */
export function voiceStartOf(clips: AudioCompositionClip[]): number {
    const starts = clips.filter((clip) => clip.role === "voice").map((clip) => clip.startTime);
    return starts.length > 0 ? Math.min(...starts) : 0;
}

/** One block of speech to validate, always tied to the clip that carries it. */
export interface TranscriptCard {
    /** Unique per card, for React keys and for editing a single card. */
    id: string;
    /** The timeline clip this card will be written back to. */
    clipId: string;
    /** Name of the clip, always shown so a block is traceable to what is heard. */
    clipName: string;
    text: string;
    voice: AudioClipVoice;
    /** Only on the first card of a clip: they all start at the same moment. */
    startTime?: number;
    /**
     * The clip arrived carrying a text of its own — a capsule transcript, or the replies
     * of a dialogue. Emptying such a clip turns narration into sound design, which is a
     * real decision and worth warning about; a music bed has nothing to lose.
     */
    hadText?: boolean;
}

/** The speech blocks a clip starts with, when it has not been transcribed yet. */
const defaultLinesFor = (track: AudioEditorTrackType): AudioClipLine[] => {
    if (track.lines && track.lines.length > 0) return track.lines;

    // A capsule that was itself a dialogue brings its own replies, each with its voice.
    if (track.sourceDialogLines && track.sourceDialogLines.length > 1) {
        return [...track.sourceDialogLines]
            .sort((a, b) => a.order - b.order)
            .map((line) => {
                const hasVoice = line.voiceAIId || line.voiceCloneId || line.voicePromptId;
                return {
                    text: line.text,
                    voiceAIId: line.voiceAIId ?? null,
                    voiceCloneId: line.voiceCloneId ?? null,
                    voicePromptId: line.voicePromptId ?? null,
                    // Only a fallback. `voiceName` is the display name of a real voice, so
                    // turning it into a custom label would both mislabel the card and
                    // store a name next to the id it duplicates.
                    voiceLabel: hasVoice ? null : (line.voiceName ?? null),
                };
            });
    }

    const text = track.sourceText ?? "";
    const voice = track.sourceVoice;
    if (!text && !voice) return [];
    return [
        {
            text,
            voiceAIId: voice?.voiceAIId ?? null,
            voiceCloneId: voice?.voiceCloneId ?? null,
            voicePromptId: voice?.voicePromptId ?? null,
            voiceLabel: voice?.voiceLabel ?? null,
        },
    ];
};

/**
 * One card per block of speech, for every clip of the mix, in the order they are heard.
 *
 * Every clip gets at least one card — even a music bed — because only the user can say
 * what carries speech. Typing a text is what makes a clip narration; leaving it empty
 * makes it sound design, which a translation keeps instead of replacing.
 *
 * Consecutive clips of the same asset share one set of cards: a clip split to remove a
 * breath is still one passage. A clip coming back after another has played gets its own,
 * since it is a genuine second turn.
 */
export function collectClipCards(tracks: AudioEditorTrackType[]): TranscriptCard[] {
    const sorted = [...tracks].sort((a, b) => (a.startTime ?? 0) - (b.startTime ?? 0));
    const cards: (TranscriptCard & { rank: number })[] = [];
    let previousKey: string | null = null;
    // Where a card lands when it carries no rank of its own: just after the last ranked
    // one, so a clip left without a text stays with the neighbours it was heard between
    // instead of being flung to one end of the list.
    let lastRank = -1;

    sorted.forEach((track) => {
        const key = clipKey(track);
        if (key && key === previousKey) return;
        previousKey = key;

        const lines = defaultLinesFor(track);
        const blocks = lines.length > 0 ? lines : [{ text: "" } as AudioClipLine];
        const hadText = lines.some((line) => (line.text ?? "").trim().length > 0);

        blocks.forEach((line, index) => {
            if (typeof line.order === "number") lastRank = line.order;
            cards.push({
                id: `${track.id}-${index}`,
                clipId: track.id,
                clipName: track.name,
                text: line.text ?? "",
                voice: {
                    voiceAIId: line.voiceAIId ?? null,
                    voiceCloneId: line.voiceCloneId ?? null,
                    voicePromptId: line.voicePromptId ?? null,
                    voiceLabel: line.voiceLabel ?? null,
                },
                startTime: index === 0 ? (track.startTime ?? 0) : undefined,
                hadText,
                rank: typeof line.order === "number" ? line.order : lastRank + 0.5,
            });
        });
    });

    // Reopening a mix has to show the transcript in the order it was validated, not in
    // timeline order — that difference is the whole point of being able to reorder.
    // Stable, so cards sharing a rank keep their timeline sequence.
    return cards
        .map((card, index) => ({ card, index }))
        .sort((a, b) => a.card.rank - b.card.rank || a.index - b.index)
        .map(({ card }) => {
            const { rank, ...rest } = card;
            return rest;
        });
}

/**
 * The capsule a brand new mix descends from, when the timeline holds exactly one voice
 * source. With several, the mix is a montage and has no single parent to inherit from.
 */
export function resolveSourceAudioAIId(tracks: AudioEditorTrackType[]): string | null {
    const sources = collectVoiceSources(tracks);
    return sources.length === 1 ? sources[0].sourceId : null;
}

/**
 * Reposition the sound design of a source language over a translated voice.
 *
 * Positions scale with the duration ratio, pivoting on where the speech starts: a sound
 * heard 20% into the narration lands 20% into the translated one. Lengths are untouched,
 * because
 * an audio file cannot be stretched without time-stretching it, and a slowed-down sound
 * effect is immediately audible. Fades travel with their clip.
 *
 * Voice clips are dropped: their cut points belong to the source signal.
 */
export function rescaleCompositionClips(
    clips: AudioCompositionClip[],
    ratio: number,
    voiceStart = 0,
): AudioCompositionClip[] {
    const safeRatio = Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
    const pivot = Number.isFinite(voiceStart) && voiceStart > 0 ? voiceStart : 0;

    return clips
        .filter((clip) => clip.role !== "voice")
        .map((clip) => {
            // Scaling pivots on where the speech starts, not on zero. What plays before
            // the first word — an intro sting, an ambience opening the piece — is not
            // timed against the narration and must not move; everything after it is.
            const startTime = clip.startTime <= pivot ? clip.startTime : pivot + (clip.startTime - pivot) * safeRatio;
            const delta = startTime - clip.startTime;
            const rescaled: AudioCompositionClip = { ...clip, startTime };
            if (clip.fadeIn) rescaled.fadeIn = { ...clip.fadeIn, start: clip.fadeIn.start + delta };
            if (clip.fadeOut) rescaled.fadeOut = { ...clip.fadeOut, start: clip.fadeOut.start + delta };
            return rescaled;
        });
}

// Function to extract the audio URL from any Audio type
export const getAudioUrl = (track: AudioAI | SoundLibraryAI | null): string | null | undefined => {
    if (!track) {
        return null;
    }

    // Check if it's a SoundLibraryAI with url property
    if (isSoundLibraryAI(track) && audioHasURL(track)) {
        return track.url;
    }

    // Check if it's an AudioAI with audioUrl property
    if (isAudioAI(track) && audioHasURL(track)) {
        return track.audioUrl;
    }

    // Fallback to null
    return null;
};

export interface SaveMixParams {
    mp3Blob: Blob;
    token: string;
    name: string;
    language: string;
    projectId: string;
    /**
     * The arrangement, which now carries what is said in each clip. The transcript of
     * the audio and its dialog lines are both derived from it server-side, so there is
     * nothing else to keep in step.
     */
    composition: AudioComposition | null;
    /**
     * When the editor was opened from an existing capsule, update that row instead of
     * adding another one to the project on every round trip.
     */
    targetAudioAIId?: string | null;
    /** Capsule the voice came from, so a later translation can find this sound design. */
    sourceAudioAIId?: string | null;
    voiceAIId?: string | null;
    voiceAISecondId?: string | null;
    voiceCloneId?: string | null;
    voiceCloneSecondId?: string | null;
    voicePromptId?: string | null;
    voicePromptSecondId?: string | null;
}

/**
 * Takes an mp3 Blob, uploads to S3 to get a URL, then either updates the audio the mix
 * was opened from, or creates a new AudioAI and attaches it to the project.
 *
 * Throws on failure: the caller shows the notification, so swallowing the error here
 * would report success on a save that never happened.
 */
export async function addMp3BlobToProject({
    mp3Blob,
    token,
    name,
    language,
    projectId,
    composition,
    targetAudioAIId,
    sourceAudioAIId,
    voiceAIId,
    voiceAISecondId,
    voiceCloneId,
    voiceCloneSecondId,
    voicePromptId,
    voicePromptSecondId,
}: SaveMixParams): Promise<string> {
    const file = new File([mp3Blob], `${name}.mp3`, { type: "audio/mp3" });

    // 1. Upload to S3
    const audioUrl = await uploadFileToS3Complete(
        {
            file,
            isPublic: true, // Public files for general uploads
            isFolder: false, // No folder structure for general uploads
        },
        token,
    );
    if (!audioUrl) throw new Error("S3 upload failed: No URL returned");

    // 2a. Re-save onto the audio this mix was opened from.
    if (targetAudioAIId) {
        await updateAudioComposition({
            token,
            audioAIId: targetAudioAIId,
            audioUrl,
            composition,
            contentName: name,
            language,
            // This save came through the transcript step, so the server may derive the
            // text and the replies from the composition.
            transcriptValidated: true,
        });
        return targetAudioAIId;
    }

    // 2b. Create the AI audio asset, with its arrangement in the same call.
    const response = await createAudioStudioAI({
        token,
        audioUrl,
        contentName: name,
        language,
        composition,
        sourceAudioAIId,
        voiceAIId,
        voiceAISecondId,
        voiceCloneId,
        voiceCloneSecondId,
        voicePromptId,
        voicePromptSecondId,
    });

    if (!response?.id) {
        throw new Error("AI audio creation failed: No ID returned");
    }

    // 3. Add item to project
    await addItemToProjectAI({
        token,
        projectId,
        itemId: response.id,
    });

    return response.id;
}

export const nocache = (url: string) => `${url}${url.includes("?") ? "&" : "?"}_ts=${Date.now()}`;

/**
 * Undo `nocache`. Clips are loaded through a cache-busted url, so the playlist reports
 * that url back — it must never be the one we store, or a stale timestamp would be
 * baked into the arrangement and defeat caching every time it is replayed.
 */
export const stripNocache = (url: string) =>
    url.replace(/([?&])_ts=\d+(&|$)/, (_match, sep, tail) =>
        sep === "?" && tail === "&" ? "?" : tail === "&" ? sep : "",
    );

export const handleAudioEditorError = (
    error: unknown,
    context: string,
    showNotification?: (notification: NotificationType) => void,
): void => {
    // Always log to console for debugging
    console.error(`${context || ""}:`, error);

    // If notification function is provided, show UI notification
    if (showNotification) {
        showNotification({
            message: context,
            type: "error",
            duration: 6000,
        });
    }
};

export const handleAudioEditorWarning = (
    message: string,
    showNotification?: (notification: NotificationType) => void,
): void => {
    // Always log to console for debugging
    console.warn(message);

    // If notification function is provided, show UI notification
    if (showNotification) {
        showNotification({
            message,
            type: "warning",
            duration: 5000,
        });
    }
};

export const handleAudioEditorInfo = (
    message: string,
    showNotification?: (notification: NotificationType) => void,
): void => {
    // Only show in the UI, no console logging needed for info
    if (showNotification) {
        showNotification({
            message,
            type: "info",
            duration: 3000,
        });
    }
};

export const handleAudioEditorSuccess = (
    message: string,
    showNotification?: (notification: NotificationType) => void,
): void => {
    if (showNotification) {
        showNotification({
            message,
            type: "success",
            duration: 3000,
        });
    }
};
